/**
 * CITY 모달 — NFT 제목·이미지 위주 그리드, 탭 시 상세(설명·가격·구매/장착).
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoClose, IoBusiness, IoChevronBack, IoSwapHorizontal } from 'react-icons/io5';
import { useAppContext } from '../../contexts/AppContext';
import { useCityEquippedCache } from '../../contexts/CityEquippedCacheContext';
import { getEnvironment } from '../../utils/environment';
import { useCityInventory } from './hooks/useCityInventory';
import { purchaseCity, equipCity, unequipCity, purchaseCitySlot } from '../../services/cityApi';
import { CITY_KEYS, CITY_GRADE, getCityGrade, getCityNameKey, getCityDescKey, sortCitiesByGrade, CITY_SLOT_UNLOCK_PRICES, CITY_SLOTS } from './constants';
import { sumCityLandmarkSpotBonusPercent } from './cityBonusUtils';


/**
 * 표시용 발행 수: 서버 mintedCount(cache/city_minted_counts 병합)와 보유 시리얼 중 큰 값.
 * 레거시 구매 등으로 캐시가 실제보다 작을 때 "남은 수량 = 총 발행" 모순을 줄임.
 * 정본 동기화는 Firestore city_tokens / cache 문서 정합성이 담당.
 */
function getDisplayMintedCount(serverMinted, ownedToken) {
  const s = Number.isFinite(serverMinted) ? serverMinted : 0;
  if (!ownedToken || ownedToken.serialNumber == null || ownedToken.serialNumber === '') return s;
  const serial = Math.floor(Number(ownedToken.serialNumber));
  if (!Number.isFinite(serial) || serial < 1) return s;
  return Math.max(s, serial);
}

function getRemainingForDisplay(maxSupply, serverMinted, ownedToken) {
  const maxS = maxSupply ?? 1000;
  const hasServer = Number.isFinite(serverMinted);
  if (!hasServer && !ownedToken) return { remaining: null, displayMinted: null };
  const displayMinted = getDisplayMintedCount(hasServer ? serverMinted : 0, ownedToken);
  return {
    displayMinted,
    remaining: Math.max(0, maxS - displayMinted),
  };
}

const CityModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { piUser, setSpotPoints, setPpPoints, spotPoints, ppPoints } = useAppContext();
  const environment = getEnvironment();
  const userId = piUser?.uid;

  const {
    configList,
    owned,
    equippedList,
    equippedTokenIds,
    cityUnlockedSlots,
    ownedCounts,
    loading,
    error,
    refetch
  } = useCityInventory(userId, environment);
  const { invalidateCache: invalidateEquippedCache } = useCityEquippedCache();

  const [selectedConfig, setSelectedConfig] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [confirmPurchase, setConfirmPurchase] = useState(null);
  const [confirmUnlock, setConfirmUnlock] = useState(null);
  /** 이미지 로드 실패한 cityId 집합 — URL은 있으나 404 등으로 표시 불가 시 아이콘 폴백 */
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [gradeFilter, setGradeFilter] = useState(CITY_GRADE.ALL);
  const visibleCities = useMemo(() => {
    const sorted = sortCitiesByGrade(configList);
    if (gradeFilter === CITY_GRADE.ALL) return sorted;
    return sorted.filter((city) => getCityGrade(city.id) === gradeFilter);
  }, [configList, gradeFilter]);

  if (!isOpen) return null;

  const getPurchaseErrorMessage = (e) => {
    const code = (e && (e.code || e.message)) || '';
    if (code === 'INSUFFICIENT_BALANCE') return t(CITY_KEYS.INSUFFICIENT_BALANCE);
    if (code === 'SOLD_OUT') return t(CITY_KEYS.SOLD_OUT);
    return t(CITY_KEYS.ERROR_GENERIC);
  };

  const doPurchase = async (cityId, paymentType) => {
    if (!userId) return;
    setActionLoading(`buy-${paymentType}-${cityId}`);
    setActionError(null);
    setSuccessMessage(null);
    try {
      const result = await purchaseCity(userId, cityId, paymentType, environment);
      setSuccessMessage(t(CITY_KEYS.SUCCESS_MESSAGE));
      refetch();
      if (paymentType === 'SPOT' && result.spotPoints !== undefined) setSpotPoints(result.spotPoints);
      if (paymentType === 'STAR' && result.ppPoints !== undefined) setPpPoints(result.ppPoints);
      setTimeout(() => setSuccessMessage(null), 2500);
    } catch (e) {
      setActionError(getPurchaseErrorMessage(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handlePurchaseClick = (cityId, paymentType, price, cityName) => {
    if (!userId) return;
    setConfirmPurchase({ cityId, paymentType, price, cityName });
  };

  const handleConfirmPurchaseOk = () => {
    if (!confirmPurchase) return;
    const { cityId, paymentType } = confirmPurchase;
    setConfirmPurchase(null);
    doPurchase(cityId, paymentType);
  };

  const handleEquip = async (tokenId, slotIndex) => {
    if (!userId) return;
    setActionLoading(`equip-${tokenId}`);
    setActionError(null);
    try {
      await equipCity(userId, tokenId, slotIndex, environment);
      invalidateEquippedCache(userId);
      refetch();
    } catch (e) {
      setActionError(e.message || 'Failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnequip = async (slotIndex) => {
    if (!userId) return;
    setActionLoading('unequip');
    setActionError(null);
    try {
      await unequipCity(userId, slotIndex, environment);
      invalidateEquippedCache(userId);
      refetch();
    } catch (e) {
      setActionError(e.message || 'Failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnlockSlot = async (slotIndex) => {
    if (!userId) return;
    const price = CITY_SLOT_UNLOCK_PRICES[slotIndex];
    if (!price || price <= 0) return;
    setConfirmUnlock({ slotIndex, price });
  };

  const handleConfirmUnlockOk = async () => {
    if (!confirmUnlock || !userId) return;
    const { slotIndex, price } = confirmUnlock;
    setConfirmUnlock(null);
    setActionLoading('unlock');
    setActionError(null);
    setSuccessMessage(null);
    try {
      await purchaseCitySlot(userId, slotIndex, environment);
      setSuccessMessage(t(CITY_KEYS.SLOT_UNLOCKED));
      if (price > 0) setPpPoints((prev) => Math.max(0, (prev || 0) - price));
      refetch();
      setTimeout(() => setSuccessMessage(null), 2500);
    } catch (e) {
      setActionError(e.message || t(CITY_KEYS.ERROR_GENERIC));
    } finally {
      setActionLoading(null);
    }
  };

  // 🎯 [거래소] 장착/판매 UI는 판매 등록 중(listed)이 아닌 active 토큰만 사용
  const getOwnedToken = (cityId) =>
    owned.find((o) => o.cityId === cityId && o.status !== 'listed') || null;

  // 🎯 [다중 구매] 도시별 보유 개수 — 서버 카운터 우선, 없으면 owned 배열에서 집계
  const getOwnedCount = (cityId) => {
    if (ownedCounts && typeof ownedCounts[cityId] === 'number') return ownedCounts[cityId];
    return owned.filter((o) => o.cityId === cityId).length;
  };

  const handleClose = () => {
    setSelectedConfig(null);
    setActionError(null);
    setSuccessMessage(null);
    setConfirmPurchase(null);
    setConfirmUnlock(null);
    onClose();
  };

  const handleCardClick = (config) => {
    // 무조건 상세 뷰 진입
    setSelectedConfig(config);
  };

  const handleComingSoon = () => {
    alert(t(CITY_KEYS.COMING_SOON_POPUP));
  };

  const isGuest = !userId;

  const getTotalBonus = () => sumCityLandmarkSpotBonusPercent(equippedList);

  const safeEquippedTokenIds = Array.isArray(equippedTokenIds) ? equippedTokenIds : [];
  const equippedListForSlots = Array.isArray(equippedList) ? equippedList : [];
  const unlockedSlotCount =
    typeof cityUnlockedSlots === 'number' && cityUnlockedSlots >= 1
      ? Math.min(3, cityUnlockedSlots)
      : 1;

  // ——— 상세 뷰 (한 카드 선택 후)
  if (selectedConfig) {
    const config = selectedConfig;
    const cityId = config.id;
    const nameKey = getCityNameKey(cityId);
    const descKey = getCityDescKey(cityId);
    const token = getOwnedToken(cityId);
    const ownedCount = getOwnedCount(cityId);
    const isEquipped = token ? safeEquippedTokenIds.includes(token.tokenId) : false;
    const currentEquipSlot = isEquipped ? safeEquippedTokenIds.indexOf(token.tokenId) : -1;
    const canBuySpot = config.priceSpot != null && config.priceSpot > 0;
    const canBuyStar = config.priceStar != null && config.priceStar > 0;
    // 🔧 [개선] mintedCount가 확인된 경우에만 SOLD OUT 판정 (null이면 미확인 → 버튼 표시 유지)
    const isSoldOut = Number.isFinite(config.mintedCount) && Number.isFinite(config.maxSupply)
      && config.mintedCount >= config.maxSupply;
    const { remaining: detailRemaining } = getRemainingForDisplay(config.maxSupply, config.mintedCount, token);
    // 🎯 [거래소 연동] STAR형 CITY(제네시스 등)는 카니발·법적 리스크 방지 목적으로 1인 1개 제한 + 거래소 등록 불가
    const isStarOnlyCity = canBuyStar && !canBuySpot;
    const starLimitReached = isStarOnlyCity && ownedCount >= 1;

    return (
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)',
          padding: 16,
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.target === e.currentTarget && handleClose()}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            maxWidth: 360,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', padding: 12, borderBottom: '1px solid #eee', gap: 8 }}>
            <button
              type="button"
              onClick={() => setSelectedConfig(null)}
              aria-label={t(CITY_KEYS.DETAIL_BACK)}
              style={{
                background: 'rgba(0,0,0,0.06)',
                border: 'none',
                borderRadius: 8,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#374151',
              }}
            >
              <IoChevronBack size={22} />
            </button>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>{t(nameKey)}</span>
          </div>

          <div style={{ padding: 20, overflowY: 'auto', flex: 1, position: 'relative' }}>
            {actionLoading && String(actionLoading).startsWith('buy-') && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: 12,
                }}
              >
                <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>{t(CITY_KEYS.PURCHASING)}</span>
              </div>
            )}
            <div
              style={{
                width: '100%',
                aspectRatio: '1',
                maxWidth: 200,
                margin: '0 auto 16px',
                borderRadius: 12,
                background: (config.imageUrl || config.thumbnailUrl) && !imageLoadErrors[cityId] ? 'transparent' : 'linear-gradient(145deg, #fef3c7 0%, #fde68a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {(config.imageUrl || config.thumbnailUrl) && !imageLoadErrors[cityId] ? (
                <img
                  src={config.imageUrl || config.thumbnailUrl}
                  alt=""
                  onError={() => setImageLoadErrors(prev => ({ ...prev, [cityId]: true }))}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <IoBusiness size={80} color="#B45309" />
              )}
            </div>
            {cityId === 'city_genesis' && (
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
                {t(descKey)}
              </p>
            )}
            <div style={{ margin: '0 0 16px', fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                {t(CITY_KEYS.BENEFIT_TITLE)}
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>{CITY_KEYS.BENEFIT_SPOT_10 && cityId === 'city_genesis' ? t(CITY_KEYS.BENEFIT_SPOT_10) : t(CITY_KEYS.BENEFIT_SPOT_5)}</li>
                {cityId === 'city_genesis' && <li>{t(CITY_KEYS.BENEFIT_FEE_20)}</li>}
                {cityId === 'city_genesis' && <li>{t(CITY_KEYS.BENEFIT_RESERVE)}</li>}
              </ul>
            </div>
            {/* 🎯 [UX] 수량 정보 명확하게 분리 표시 */}
            <div style={{ margin: '0 0 16px', background: '#F9FAFB', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#6B7280', lineHeight: 1.8, border: '1px solid #E5E7EB' }}>
              {token && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#374151', fontWeight: 600 }}>📌 {t(CITY_KEYS.MY_SERIAL_LABEL)}</span>
                  <span style={{ color: '#6C5CE7', fontWeight: 700 }}>No.{token.serialNumber}</span>
                </div>
              )}
              {ownedCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#374151', fontWeight: 600 }}>📦 {t(CITY_KEYS.BADGE_OWNED)}</span>
                  <span style={{ color: '#3b82f6', fontWeight: 700 }}>
                    {t(CITY_KEYS.NFT_COUNT_FORMATTED, { count: ownedCount })}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{t(CITY_KEYS.TOTAL_SUPPLY_LABEL)}</span>
                <span style={{ fontWeight: 600, color: '#374151' }}>
                  {t(CITY_KEYS.NFT_COUNT_FORMATTED, { count: (config.maxSupply ?? 1000).toLocaleString() })}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{t(CITY_KEYS.REMAINING_LABEL)}</span>
                <span style={{
                  fontWeight: 600,
                  color: detailRemaining === null ? '#6B7280' : detailRemaining === 0 ? '#DC2626' : '#059669',
                }}
                >
                  {detailRemaining === null
                    ? '-'
                    : t(CITY_KEYS.NFT_COUNT_FORMATTED, { count: detailRemaining.toLocaleString() })}
                </span>
              </div>
            </div>
            {userId && successMessage && (
              <p style={{ margin: '0 0 12px', color: '#059669', fontSize: 14, fontWeight: 600 }}>{successMessage}</p>
            )}
            {userId && actionError && (
              <p style={{ margin: '0 0 12px', color: '#dc2626', fontSize: 14 }}>{actionError}</p>
            )}
            {isGuest && (
              <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>{t(CITY_KEYS.LOGIN_TO_USE)}</p>
            )}
            {!isGuest && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* 🎯 [다중 구매] 보유 중이어도 장착 관리(위)와 구매(아래)를 함께 노출 — 여러 개 보유 가능 */}
                {/* 🎯 [거래소] 판매 등록 중인 토큰은 장착 대상에서 제외(서버에서도 active 상태만 허용) */}
                {token && token.status !== 'listed' && (
                  isEquipped ? (
                    <button
                      type="button"
                      disabled={!!actionLoading}
                      onClick={() => handleUnequip(currentEquipSlot)}
                      style={{
                        padding: '10px 16px',
                        fontSize: 14,
                        border: '1px solid #d1d5db',
                        borderRadius: 10,
                        background: '#f3f4f6',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        width: '100%',
                      }}
                    >
                      {t(CITY_KEYS.UNEQUIP_WITH_SLOT, { n: currentEquipSlot + 1 })}
                    </button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                      {Array.from({ length: CITY_SLOTS }).map((_, i) => {
                        if (i >= unlockedSlotCount) return null;
                        return (
                          <button
                            key={'equip-slot-' + i}
                            type="button"
                            disabled={!!actionLoading}
                            onClick={() => handleEquip(token.tokenId, i)}
                            style={{
                              padding: '10px 16px',
                              fontSize: 14,
                              border: 'none',
                              borderRadius: 10,
                              background: '#3b82f6',
                              color: '#fff',
                              cursor: actionLoading ? 'not-allowed' : 'pointer',
                              width: '100%',
                            }}
                          >
                            {t(CITY_KEYS.BTN_EQUIP_SLOT, { n: i + 1 })}
                          </button>
                        );
                      })}
                    </div>
                  )
                )}
                {isSoldOut ? (
                  // 🔧 SOLD OUT 시 구매 버튼 대신 배지 표시 (이미 보유한 토큰의 장착 관리는 위에서 계속 가능)
                  <div style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: '#FEE2E2',
                    border: '1px solid #FCA5A5',
                    borderRadius: 10,
                    textAlign: 'center',
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#DC2626',
                    letterSpacing: '0.05em'
                  }}>
                    📦 {t(CITY_KEYS.SOLD_OUT)}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {canBuySpot && (
                      <button
                        type="button"
                        disabled={!!actionLoading}
                        onClick={() => handlePurchaseClick(cityId, 'SPOT', config.priceSpot, t(nameKey))}
                        style={{
                          padding: '10px 16px',
                          fontSize: 14,
                          border: 'none',
                          borderRadius: 10,
                          background: '#10b981',
                          color: '#fff',
                          cursor: actionLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {t(CITY_KEYS.PRICE_SPOT, { value: config.priceSpot })}
                      </button>
                    )}
                    {canBuyStar && !starLimitReached && (
                      <button
                        type="button"
                        disabled={!!actionLoading}
                        onClick={() => handlePurchaseClick(cityId, 'STAR', config.priceStar, t(nameKey))}
                        style={{
                          padding: '10px 16px',
                          fontSize: 14,
                          border: 'none',
                          borderRadius: 10,
                          background: '#8b5cf6',
                          color: '#fff',
                          cursor: actionLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {t(CITY_KEYS.PRICE_STAR, { value: config.priceStar })}
                      </button>
                    )}
                    {/* 🎯 [거래소 연동] STAR형 CITY 1인 1개 제한 도달 시 구매 버튼 대신 안내 배지 */}
                    {starLimitReached && (
                      <div style={{
                        padding: '10px 16px',
                        fontSize: 13,
                        fontWeight: 700,
                        borderRadius: 10,
                        background: '#F3F4F6',
                        color: '#6B7280',
                      }}>
                        🔒 {t(CITY_KEYS.STAR_LIMIT_BADGE)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {confirmPurchase && (
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.4)',
              borderRadius: 16,
              zIndex: 20,
            }}
            onClick={() => setConfirmPurchase(null)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: 20,
                maxWidth: 320,
                width: '100%',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>
                {t(CITY_KEYS.CONFIRM_TITLE)}
              </h3>
              <p style={{ margin: '0 0 8px', fontSize: 14, color: '#4b5563', lineHeight: 1.5 }}>
                {t(CITY_KEYS.CONFIRM_MESSAGE, {
                  name: confirmPurchase.cityName,
                  price: `${Number(confirmPurchase.price).toLocaleString()} ${confirmPurchase.paymentType === 'SPOT' ? 'SPOT' : 'STAR'}`,
                })}
              </p>
              <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
                {t(CITY_KEYS.CONFIRM_CURRENT_BALANCE, {
                  amount: (confirmPurchase.paymentType === 'SPOT' ? (spotPoints ?? 0) : (ppPoints ?? 0)).toLocaleString(),
                  unit: confirmPurchase.paymentType === 'SPOT' ? 'SPOT' : 'STAR',
                })}
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setConfirmPurchase(null)}
                  style={{
                    padding: '10px 16px',
                    fontSize: 14,
                    border: '1px solid #d1d5db',
                    borderRadius: 10,
                    background: '#f9fafb',
                    color: '#374151',
                    cursor: 'pointer',
                  }}
                >
                  {t(CITY_KEYS.CONFIRM_CANCEL)}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPurchaseOk}
                  style={{
                    padding: '10px 16px',
                    fontSize: 14,
                    border: 'none',
                    borderRadius: 10,
                    background: '#10b981',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {t(CITY_KEYS.CONFIRM_OK)}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ——— 목록 뷰 (제목·이미지 위주 그리드)
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="city-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
        padding: 16,
        boxSizing: 'border-box',
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          maxWidth: 400,
          width: '100%',
          height: '85vh',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottom: '1px solid #eee', flexShrink: 0 }}>
          <h2 id="city-modal-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1a1a1a' }}>
            {t(CITY_KEYS.MODAL_TITLE)}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t(CITY_KEYS.MODAL_CLOSE)}
              style={{
                background: 'rgba(0,0,0,0.06)',
                border: 'none',
                borderRadius: 8,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#374151',
                marginLeft: 4,
              }}
            >
              <IoClose size={20} />
            </button>
          </div>
        </div>

        <div style={{ flexShrink: 0, padding: '10px 16px 8px', borderBottom: '1px solid #eef2f7' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{t(CITY_KEYS.SLOT_TITLE)}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981' }}>{t(CITY_KEYS.ACTIVE_BONUS)}{getTotalBonus()}%</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {Array.from({ length: CITY_SLOTS }).map((_, i) => {
              const item = equippedListForSlots[i];
              const isUnlocked = i < unlockedSlotCount;
              return (
                <div
                  key={`slot-${i}`}
                  onClick={() => !isUnlocked ? handleUnlockSlot(i) : null}
                  style={{
                    flex: 1,
                    height: 56,
                    borderRadius: 10,
                    background: isUnlocked ? (item ? '#fff' : '#f3f4f6') : '#e5e7eb',
                    border: isUnlocked ? (item ? '2px solid #3b82f6' : '2px dashed #d1d5db') : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: !isUnlocked ? 'pointer' : 'default',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {!isUnlocked ? (
                    <div style={{ color: '#9ca3af', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <span style={{ fontSize: 14 }}>🔒</span>
                      <span style={{ fontSize: 9, fontWeight: 700 }}>{t(CITY_KEYS.PRICE_STAR, { value: CITY_SLOT_UNLOCK_PRICES[i].toLocaleString() })}</span>
                    </div>
                  ) : item ? (
                    <img src={item.thumbnailUrl || item.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 16, color: '#9ca3af', opacity: 0.5 }}>+</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {(userId && (error || actionError || successMessage)) || (!userId && configList.length > 0) ? (
          <div style={{ flexShrink: 0, padding: '8px 16px 0' }}>
            {userId && error && <p style={{ margin: '0 0 6px', color: '#dc2626', fontSize: 12 }}>{error}</p>}
            {userId && actionError && <p style={{ margin: '0 0 6px', color: '#dc2626', fontSize: 12 }}>{actionError}</p>}
            {userId && successMessage && <p style={{ margin: '0 0 6px', color: '#059669', fontSize: 12, fontWeight: 600 }}>{successMessage}</p>}
            {!userId && configList.length > 0 && (
              <p style={{ margin: '0 0 6px', fontSize: 12, color: '#6B7280', lineHeight: 1.4 }}>
                {t(CITY_KEYS.LOGIN_TO_USE)}
              </p>
            )}
          </div>
        ) : null}

        <div style={{ flexShrink: 0, display: 'flex', gap: 6, padding: '10px 16px 0' }} role="tablist">
          {[
            [CITY_GRADE.ALL, CITY_KEYS.GRADE_ALL],
            [CITY_GRADE.SPECIAL, CITY_KEYS.GRADE_SPECIAL],
            [CITY_GRADE.MAJOR, CITY_KEYS.GRADE_MAJOR],
            [CITY_GRADE.STANDARD, CITY_KEYS.GRADE_STANDARD]
          ].map(([grade, labelKey]) => {
            const selected = gradeFilter === grade;
            return (
              <button
                key={grade}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setGradeFilter(grade)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: selected ? '1px solid #6C5CE7' : '1px solid #E5E7EB',
                  borderRadius: 999,
                  padding: '7px 4px',
                  background: selected ? '#6C5CE7' : '#F9FAFB',
                  color: selected ? '#FFFFFF' : '#4B5563',
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {t(labelKey)}
              </button>
            );
          })}
        </div>

        <div style={{ padding: '12px 16px 16px', overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {configList.length === 0 && loading ? (
            <p style={{ margin: 'auto', color: '#6B7280' }}>Loading...</p>
          ) : visibleCities.length === 0 ? (
            <p style={{ margin: 'auto', color: '#6B7280', fontSize: 13, fontWeight: 700, textAlign: 'center', padding: '28px 8px' }}>
              {t(CITY_KEYS.GRADE_EMPTY)}
            </p>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
                flexShrink: 0,
              }}
            >
              {visibleCities.map((config) => {
                const cityId = config.id;
                const nameKey = getCityNameKey(cityId);
                const token = getOwnedToken(cityId);
                const ownedCount = getOwnedCount(cityId);
                const isGenesis = cityId === 'city_genesis';
                const isEquipped = owned.some((item) => item.cityId === cityId && safeEquippedTokenIds.includes(item.tokenId));
                const { remaining: cardRemaining } = getRemainingForDisplay(config.maxSupply, config.mintedCount, token);
                const priceLabel = config.priceSpot > 0
                  ? t(CITY_KEYS.PRICE_SPOT, { value: Number(config.priceSpot).toLocaleString() })
                  : config.priceStar > 0
                    ? t(CITY_KEYS.PRICE_STAR, { value: Number(config.priceStar).toLocaleString() })
                    : null;

                return (
                  <button
                    key={cityId}
                    type="button"
                    onClick={() => handleCardClick(config)}
                    style={{
                      border: isEquipped
                        ? '2px solid #3b82f6'
                        : '1px solid #e5e7eb',
                      borderRadius: 12,
                      padding: 0,
                      overflow: 'hidden',
                      background: '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '1',
                        position: 'relative',
                        background: (config.thumbnailUrl || config.imageUrl) && !imageLoadErrors[cityId]
                          ? 'transparent'
                          : isGenesis
                            ? 'linear-gradient(145deg, #c4b5fd 0%, #a78bfa 100%)'
                            : 'linear-gradient(145deg, #fef3c7 0%, #fde68a 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {/* 🎯 [다중 구매] 보유 개수 배지 — 장착 여부와 무관하게 보유 중이면 항상 표시 */}
                      {ownedCount > 0 && (
                        <div style={{
                          position: 'absolute', top: 3, left: 3, zIndex: 1,
                          background: 'rgba(59, 130, 246, 0.9)',
                          color: 'white', fontSize: 8, fontWeight: 700,
                          padding: '1px 4px', borderRadius: 4,
                          maxWidth: '70%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {t(CITY_KEYS.BADGE_OWNED)} {t(CITY_KEYS.NFT_COUNT_FORMATTED, { count: ownedCount })}
                        </div>
                      )}
                      {(config.thumbnailUrl || config.imageUrl) && !imageLoadErrors[cityId] ? (
                        <img
                          src={config.thumbnailUrl || config.imageUrl}
                          alt=""
                          onError={() => setImageLoadErrors(prev => ({ ...prev, [cityId]: true }))}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <IoBusiness size={36} color={isGenesis ? '#5b21b6' : '#B45309'} />
                      )}
                      {(() => {
                        const max = config.maxSupply ?? 1000;
                        const remainingForSoldOut = cardRemaining ?? max;
                        if (remainingForSoldOut <= 0) {
                          return (
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(0,0,0,0.6)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 'inherit',
                              }}
                            >
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.05em' }}>
                                {t(CITY_KEYS.SOLD_OUT)}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div style={{ padding: '6px 4px 8px', textAlign: 'center' }}>
                      <div style={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: '#1a1a1a',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>{t(nameKey)}</div>
                      {priceLabel && (
                        <div style={{
                          fontSize: 11,
                          fontWeight: 800,
                          marginTop: 2,
                          color: config.priceSpot > 0 ? '#059669' : '#7c3aed',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {priceLabel}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {confirmUnlock && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 20,
          }}
          onClick={() => setConfirmUnlock(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: 20,
              maxWidth: 320,
              width: '100%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>
              {t(CITY_KEYS.CONFIRM_TITLE)}
            </h3>
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#4b5563', lineHeight: 1.5 }}>
              {t(CITY_KEYS.SLOT_UNLOCK_CONFIRM, { cost: Number(confirmUnlock.price).toLocaleString() })}
            </p>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
              {t(CITY_KEYS.CONFIRM_CURRENT_BALANCE, {
                amount: (ppPoints ?? 0).toLocaleString(),
                unit: 'STAR',
              })}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setConfirmUnlock(null)}
                style={{
                  padding: '10px 16px',
                  fontSize: 14,
                  border: '1px solid #d1d5db',
                  borderRadius: 10,
                  background: '#f9fafb',
                  color: '#374151',
                  cursor: 'pointer',
                }}
              >
                {t(CITY_KEYS.CONFIRM_CANCEL)}
              </button>
              <button
                type="button"
                onClick={handleConfirmUnlockOk}
                style={{
                  padding: '10px 16px',
                  fontSize: 14,
                  border: 'none',
                  borderRadius: 10,
                  background: '#10b981',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {t(CITY_KEYS.CONFIRM_OK)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityModal;
